"use client";

import { useState } from "react";

import AddDose from "@/components/dose/add-dose";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import Button from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { Label } from "@/components/ui/label";

import { useUser } from "@/contexts/user-provider";

import { useLaunchDoseSpot } from "@/hooks/use-launch-dosespot";
import { useModule } from "@/hooks/use-module";

import { toast } from "@/lib/use-toast";

import { MedicationSummary } from "@/types/medication";
import { NoteDetail, NoteListItem } from "@/types/notes";
import { PatientDetail } from "@/types/patient";

interface AddDoseWrapperProps {
  patientId: string;
  patient?: PatientDetail;
  selectedNote?: NoteListItem | NoteDetail | null;
  /** Appointment the dose is logged against, e.g. from the appointment checklist. */
  eventId?: string | null;
  selectedMedicationType?: string;
  mostRecentDose?: MedicationSummary | null;
  onSubmit: (data: MedicationSummary) => void;
  children: React.ReactNode;
  align?: "start" | "end";
  locationId?: string;
  /** If true, renders menu items directly instead of wrapping in a dropdown */
  renderAsMenuItems?: boolean;
}

/**
 * Wrapper component for adding medication doses.
 * If e-prescribing module is enabled, shows a dropdown with options to:
 * - Add Dose (platform)
 * - E-prescribe
 * If e-prescribing module is not enabled, directly opens the AddDose dialog.
 */
export default function AddDoseWrapper({
  patientId,
  patient,
  selectedNote,
  eventId,
  selectedMedicationType,
  mostRecentDose,
  onSubmit,
  children,
  align = "start",
  locationId,
  renderAsMenuItems = false,
}: AddDoseWrapperProps) {
  const hasDoseSpotModule = useModule("DOSESPOT");
  const { currentUser } = useUser();
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [menuOpen, setMenuOpen] = useState(false);

  const { launchDoseSpot, dosespotLocations, getBestLocationId } =
    useLaunchDoseSpot({
      locationId,
      patient,
      userId: currentUser?.dosespotUserId,
      openInNewTab: true,
    });

  const handleOpenDoseSpot = async () => {
    // If no locations with DoseSpot configured, show error
    if (dosespotLocations.length === 0) {
      toast({
        title: "Error",
        description:
          "No location with e-prescribing configured found. Please configure e-prescribing credentials in location settings.",
        variant: "error",
      });
      return;
    }

    // If only one location, use it directly
    if (dosespotLocations.length === 1) {
      await launchDoseSpot(patientId, dosespotLocations[0].id);
      return;
    }

    // Multiple locations - show dialog with suggested location pre-selected
    const suggestedLocationId = locationId || getBestLocationId();
    if (suggestedLocationId) {
      setSelectedLocationId(suggestedLocationId);
    } else {
      // Pre-select first location if no suggestion
      setSelectedLocationId(dosespotLocations[0].id);
    }

    setShowLocationDialog(true);
  };

  const handleLaunchDoseSpot = async () => {
    if (!selectedLocationId) {
      toast({
        title: "Error",
        description: "Please select a location",
        variant: "error",
      });
      return;
    }

    setShowLocationDialog(false);
    await launchDoseSpot(patientId, selectedLocationId);
  };

  // If DoseSpot module is not enabled, just render AddDose directly
  if (!hasDoseSpotModule) {
    return (
      <AddDose
        patientId={patientId}
        selectedNote={selectedNote}
        eventId={eventId}
        selectedMedicationType={selectedMedicationType}
        mostRecentDose={mostRecentDose}
        onSubmit={onSubmit}
      >
        {children}
      </AddDose>
    );
  }

  // If renderAsMenuItems is true, render menu items directly (for use inside existing dropdown)
  if (renderAsMenuItems) {
    return (
      <>
        <AddDose
          patientId={patientId}
          selectedNote={selectedNote}
          eventId={eventId}
          selectedMedicationType={selectedMedicationType}
          mostRecentDose={mostRecentDose}
          onSubmit={onSubmit}
        >
          {children}
        </AddDose>

        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            handleOpenDoseSpot();
          }}
          className="cursor-pointer"
        >
          E-prescribe
        </DropdownMenuItem>

        <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Select Location for DoseSpot</DialogTitle>
              <p className="text-body text-muted-foreground">
                Choose a location with DoseSpot configured to open e-prescribing
              </p>
            </DialogHeader>
            <DialogBody>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="dosespot-location">Location</Label>
                  <Select
                    value={selectedLocationId}
                    onValueChange={setSelectedLocationId}
                  >
                    <SelectTrigger id="dosespot-location">
                      <SelectValue placeholder="Select a location" />
                    </SelectTrigger>
                    <SelectContent>
                      {dosespotLocations.length === 0 ? (
                        <div className="px-2 py-2 text-body text-muted-foreground">
                          No locations with e-prescribing configured
                        </div>
                      ) : (
                        dosespotLocations.map((location) => (
                          <SelectItem key={location.id} value={location.id}>
                            {location.name}
                            <span>
                              {location.isVirtual ? " (Virtual)" : ""}
                            </span>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                onClick={() => setShowLocationDialog(false)}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleLaunchDoseSpot}
                disabled={!selectedLocationId}
              >
                E-prescribe
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // If DoseSpot module is enabled, show dropdown with both options
  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
        <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
        <DropdownMenuContent align={align}>
          {/* The dialog lives inside the menu, so the menu stays open behind it until the dose saves. */}
          <AddDose
            patientId={patientId}
            selectedNote={selectedNote}
            eventId={eventId}
            selectedMedicationType={selectedMedicationType}
            mostRecentDose={mostRecentDose}
            onSubmit={(dose) => {
              onSubmit(dose);
              setMenuOpen(false);
            }}
          >
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              Add Dose
            </DropdownMenuItem>
          </AddDose>

          <DropdownMenuItem
            onSelect={() => handleOpenDoseSpot()}
            className="cursor-pointer"
          >
            E-prescribe
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Select Location for E-prescribing</DialogTitle>
            <p className="text-body text-muted-foreground">
              Choose a location with e-prescribing configured
            </p>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dosespot-location">Location</Label>
                <Select
                  value={selectedLocationId}
                  onValueChange={setSelectedLocationId}
                >
                  <SelectTrigger id="dosespot-location">
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {dosespotLocations.length === 0 ? (
                      <div className="px-2 py-2 text-body text-muted-foreground">
                        No locations with e-prescribing configured
                      </div>
                    ) : (
                      dosespotLocations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name}
                          <span>{location.isVirtual ? " (Virtual)" : ""}</span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              onClick={() => setShowLocationDialog(false)}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleLaunchDoseSpot}
              disabled={!selectedLocationId}
            >
              E-prescribe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
